<template>
    <div class="suspended-warning-container">
        <div class="warning-card">
            <div class="card-body">
                <div class="card-info">
                    <div class="alert-icon">
                        <svg
                            viewBox="0 0 24 24"
                            width="32"
                            height="32"
                            stroke="#e74c3c"
                            stroke-width="2"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                    </div>
                    <div class="card-message">
                        <h3>{{ suspendTitle }}</h3>
                        <p>{{ suspendDescription }}</p>
                        <div v-if="suspendDate" class="suspend-date">
                            <strong>{{ $t("message.suspendDateLabel") }}:</strong> {{ formatDate(suspendDate) }}
                        </div>
                    </div>
                </div>

                <div v-if="showActions" class="card-actions">
                    <SupsisButton 
                        v-if="suspendReason === 'payment'" 
                        primary 
                        @click="goToBilling"
                    >
                        {{ $t("message.makePayment") }}
                    </SupsisButton>
                    <SupsisButton ghost @click="connectSupport">
                        {{ $t("message.liveSupportButton") }}
                    </SupsisButton>
                </div>

                <div v-if="!showActions" class="no-permission-info">
                    <p>{{ $t("message.noPermissionContactAdmin") }}</p>
                    <div class="support-action">
                        <SupsisButton primary @click="connectSupport">
                            {{ $t("message.liveSupportButton") }}
                        </SupsisButton>
                    </div>
                </div>
            </div>
        </div>

        <div class="features-container">
            <div class="feature-box">
                <div class="feature-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22272d"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                </div>
                <h4>{{ $t("message.paymentProblemSolution") }}</h4>
                <p>{{ $t("message.paymentProblemSolutionDesc") }}</p>
            </div>

            <div class="feature-box">
                <div class="feature-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22272d"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                </div>
                <h4>{{ $t("message.quickSolution") }}</h4>
                <p>{{ $t("message.quickSolutionDesc") }}</p>
            </div>

            <div class="feature-box">
                <div class="feature-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22272d"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M9 11H1l6-6 6 6M9 17h8l-6 6-6-6"></path>
                    </svg>
                </div>
                <h4>{{ $t("message.liveSupportTitle") }}</h4>
                <p>{{ $t("message.liveSupportDesc") }}</p>
            </div>
        </div>


    </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { publishMessage, SystemMessageTypes } from "../utils/systemPubSub";
import SupsisButton from "../views/Pages/Settings/components/SupsisButton.vue";

export default {
    name: "SuspendedWarning",
    components: {
        SupsisButton,
    },
    computed: {
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        ...mapState({
            isAdmin: (state) => state.auth.adminUser != null,
            isRoot: (state) => state.auth.user.role.isRoot,
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            suspendReason: (state) => state.site.site.suspendReason,
            suspendDate: (state) => state.site.site.suspendDate,
        }),
        hasPermission() {
            return this.isAdmin || this.isRoot || this.checkPermission("billing.read");
        },
        showActions() {
            return this.hasPermission && this.suspendReason === 'payment';
        },
        suspendTitle() {
            switch (this.suspendReason) {
                case 'payment':
                    return "Ödeme Sorunu Nedeniyle Hesap Askıda";
                case 'trial-expired':
                    return "Deneme Süresi Sona Erdi";
                case 'admin-suspend':
                    return "Hesap Yönetici Tarafından Askıya Alındı";
                default:
                    return "Hesap Askıya Alındı";
            }
        },
        suspendDescription() {
            switch (this.suspendReason) {
                case 'payment':
                    return "Hesabınız ödeme sorunu nedeniyle askıya alınmıştır. Birikmiş faturalarınızı ödeyerek ve kredi kartı bilgilerinizi ekleyerek hesabınızı tekrar aktifleştirebilirsiniz.";
                case 'trial-expired':
                    return "Ücretsiz deneme süreniz sona ermiştir. Supsis'i kullanmaya devam etmek için abonelik planlarımızdan birini seçmeniz gerekmektedir.";
                case 'admin-suspend':
                    return "Hesabınız sistem yöneticisi tarafından askıya alınmıştır. Bu durumla ilgili detaylı bilgi almak için lütfen canlı destek ile iletişime geçin.";
                default:
                    return "Hesabınız askıya alınmıştır. Daha fazla bilgi için canlı destek ile iletişime geçebilirsiniz.";
            }
        },
    },
    data() {
        return {};
    },
    methods: {
        connectSupport() {
            const initialMessages = {
                'payment': 'Ödeme sorunu nedeniyle askıya alınan hesabım hakkında yardıma ihtiyacım var',
                'trial-expired': 'Deneme sürem sona erdiği için askıya alınan hesabım hakkında yardıma ihtiyacım var',
                'admin-suspend': 'Yönetici tarafından askıya alınan hesabım hakkında bilgi almak istiyorum',
                'other': 'Askıya alınan hesabım hakkında yardıma ihtiyacım var'
            };

            publishMessage(SystemMessageTypes.CONNECT_SUPPORT_TEAM, {
                initialMessage: initialMessages[this.suspendReason] || initialMessages['other'],
                largeMode: true,
            });
        },
        goToBilling() {
            this.$router.push('/settings/billing?focus=invoice');
        },
        formatDate(date) {
            if (!date) return '';
            return this.$moment(date).format('DD.MM.YYYY');
        },
    },
};
</script>

<style scoped>
.suspended-warning-container {
    width: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    height: 100%;
}

.warning-card {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    width: 100%;
    max-width: 900px;
    margin-bottom: 40px;
}

.card-body {
    padding: 24px;
}

.card-info {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
}

.alert-icon {
    margin-right: 16px;
    flex-shrink: 0;
}

.card-message h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    color: #333;
    font-weight: 600;
}

.card-message p {
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: #555;
}

.suspend-date {
    font-size: 14px;
    color: #666;
    background-color: #f8f9fa;
    padding: 8px 12px;
    border-radius: 4px;
    margin-top: 8px;
}

.suspend-date strong {
    color: #333;
}

.card-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-start;
    margin-left: 48px;
}

.no-permission-info {
    margin-left: 48px;
    border-top: 1px solid #eee;
    padding-top: 16px;
    margin-top: 16px;
}

.no-permission-info p {
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #555;
}

.support-action {
    display: flex;
    justify-content: flex-start;
}

.features-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
    width: 100%;
    max-width: 900px;
}

.feature-box {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 20px;
    width: 100%;
    max-width: 280px;
    text-align: center;
    transition: transform 0.2s;
}

.feature-box:hover {
    transform: translateY(-5px);
}

.feature-icon {
    margin-bottom: 15px;
}

h4 {
    margin: 0 0 10px 0;
    font-size: 16px;
    color: #22272d;
    font-weight: 600;
}

.feature-box p {
    font-size: 14px;
    color: #666;
    margin: 0;
}



strong {
    color: #e74c3c;
    font-weight: 600;
}

/* Responsive design */
@media (max-width: 768px) {
    .suspended-warning-container {
        padding: 15px;
    }
    
    .warning-card {
        margin-bottom: 20px;
    }
    
    .card-actions {
        margin-left: 0;
        justify-content: center;
    }
    
    .no-permission-info {
        margin-left: 0;
    }
    
    .card-info {
        flex-direction: column;
        text-align: center;
    }
    
    .alert-icon {
        margin-right: 0;
        margin-bottom: 12px;
        display: flex;
        justify-content: center;
    }
}
</style>
